/** Typed API helpers para Cadastros (F2). */
import { api } from "@/lib/api";

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

export interface Cidade {
  id: string;
  name: string;
  uf: string;
  ibge_code: string | null;
  lat: number | null;
  lng: number | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Address {
  id: string;
  postal_code: string;
  street: string;
  number: string | null;
  complement: string | null;
  district: string | null;
  cidade_id: string;
  cidade: Cidade | null;
  lat: number | null;
  lng: number | null;
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  parent_id: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Client {
  id: string;
  corporate_name: string;
  trade_name: string;
  cnpj: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  notes: string | null;
  address_id: string | null;
  address: Address | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export type NerdStatus = "pending" | "approved" | "suspended" | "rejected";

export interface Nerd {
  id: string;
  user_id: string;
  cpf: string | null;
  cnpj: string | null;
  legal_name: string | null;
  status: NerdStatus;
  rank_score: number;
  completed_jobs: number;
  working_radius_km: number;
  bio: string | null;
  address: Address | null;
  user_email: string | null;
  user_name: string | null;
  user_picture_url: string | null;
  display_name: string | null;
  created_at: string;
  updated_at: string;
}

export type ContractType = "bodyshop" | "avulso" | "credito" | "projetos" | "baseline";

export interface Contract {
  id: string;
  client_id: string;
  name: string;
  type: ContractType;
  starts_at: string;
  ends_at: string | null;
  active: boolean;
  monthly_credit_limit: number | null;
  monthly_credit_used: number;
  sla_hours: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// ── Generic CRUD wrapper ──────────────────────────────────────────────────
function crud<T, C, U>(base: string) {
  return {
    list: async (params: Record<string, unknown> = {}): Promise<Page<T>> => {
      const { data } = await api.get<Page<T>>(base, { params });
      return data;
    },
    get: async (id: string): Promise<T> => {
      const { data } = await api.get<T>(`${base}/${id}`);
      return data;
    },
    create: async (body: C): Promise<T> => {
      const { data } = await api.post<T>(base, body);
      return data;
    },
    update: async (id: string, body: U): Promise<T> => {
      const { data } = await api.patch<T>(`${base}/${id}`, body);
      return data;
    },
    remove: async (id: string): Promise<void> => {
      await api.delete(`${base}/${id}`);
    },
  };
}

export const cidadesApi = crud<Cidade, Partial<Cidade>, Partial<Cidade>>("/v1/cidades");
export const categoriesApi = crud<Category, Partial<Category>, Partial<Category>>("/v1/categories");
export const clientsApi = crud<Client, Partial<Client>, Partial<Client>>("/v1/clients");
export const nerdsApi = crud<Nerd, Partial<Nerd>, Partial<Nerd>>("/v1/nerds");
export const contractsApi = crud<Contract, Partial<Contract>, Partial<Contract>>("/v1/contracts");

// ── Onboarding ────────────────────────────────────────────────────────────
export async function completeOnboarding(choice: "nerd" | "client", extras: { name?: string; phone?: string } = {}) {
  const { data } = await api.post("/v1/auth/onboarding", { choice, ...extras });
  return data;
}
